import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { ChevronLeft, Trophy, BarChart3 } from "lucide-react";
import { api } from "@/lib/handball";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";

function safeFormatDate(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return format(d, "d MMM yyyy", { locale: sv });
  } catch {
    return "";
  }
}

/**
 * TournamentDetailPage — opens from TournamentsPage. Shows:
 *  - Tournament header
 *  - Goalkeeper ranking (per-GK aggregated stats across tournament sessions)
 *  - Session list
 */
export default function TournamentDetailPage() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/tournaments/${tournamentId}/sessions`);
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Kunde inte ladda turneringen");
      } finally {
        setLoading(false);
      }
    })();
  }, [tournamentId]);

  const topSavePct = data?.goalkeepers?.[0]?.save_pct || 0;

  return (
    <div className="min-h-screen">
      <AppHeader
        subtitle={data?.tournament?.name || "Turnering"}
        backTo="/tournaments"
        backLabel="Turneringar"
      />
      <main className="max-w-[1100px] mx-auto px-6 py-10 space-y-8">
        <button
          type="button"
          onClick={() => navigate("/tournaments")}
          className="flex items-center gap-1 text-zinc-400 hover:text-white"
          data-testid="tournament-detail-back"
        >
          <ChevronLeft size={16} /> Alla turneringar
        </button>

        {loading ? (
          <div className="text-zinc-500 font-display uppercase tracking-widest">
            Laddar…
          </div>
        ) : error ? (
          <div className="card-tactical p-8 text-center">
            <div className="text-zinc-400">{error}</div>
          </div>
        ) : (
          <>
            {/* Header */}
            <section className="card-tactical p-6 flex items-center gap-4 flex-wrap">
              <div className="w-14 h-14 border-2 border-[#FFD60A] flex items-center justify-center bg-[#171717]">
                <Trophy size={26} className="text-[#FFD60A]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
                  Turnering / serie
                </div>
                <div
                  className="font-display font-black uppercase tracking-widest text-3xl text-white leading-tight"
                  data-testid="tournament-detail-name"
                >
                  {data.tournament.name}
                </div>
                <div className="text-xs text-zinc-500 mt-1">
                  {safeFormatDate(data.tournament.start_date + "T00:00:00") || data.tournament.start_date}
                  {" — "}
                  {safeFormatDate(data.tournament.end_date + "T00:00:00") || data.tournament.end_date}
                </div>
              </div>
            </section>

            {/* Goalkeeper ranking */}
            {data.goalkeepers.length === 0 ? (
              <div className="card-tactical p-10 text-center">
                <div className="text-zinc-400">
                  Inga matcher loggade i turneringen ännu.
                </div>
              </div>
            ) : (
              <section className="card-tactical p-6">
                <div className="section-hdr mb-5">
                  <span className="marker" />
                  Jämförelse — målvakter
                </div>
                <div className="space-y-3" data-testid="tournament-gk-ranking">
                  {data.goalkeepers.map((g, idx) => {
                    const pctW = topSavePct > 0 ? (g.save_pct / topSavePct) * 100 : 0;
                    return (
                      <div
                        key={g.id}
                        data-testid={`tournament-gk-row-${g.id}`}
                        className="border border-[#27272A] p-4"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-7 h-7 border border-[#3F3F46] flex items-center justify-center bg-black text-xs font-display font-black text-white">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display uppercase tracking-widest text-white text-lg truncate">
                              {g.name || "Okänd"}
                            </div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">
                              {g.sessions_played} match{g.sessions_played === 1 ? "" : "er"}
                              {" · "}
                              {g.saves}/{g.total} räddningar
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-display font-black text-2xl text-[#34C759] leading-none">
                              {g.save_pct.toFixed(1)}%
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                              räddnings%
                            </div>
                          </div>
                          <Button
                            data-testid={`tournament-gk-eval-${g.id}`}
                            onClick={() => navigate(`/gk/${g.id}/evaluate/tournament/${tournamentId}`)}
                            variant="outline"
                            className="h-9 rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white text-xs"
                          >
                            <BarChart3 size={14} className="mr-1.5" />
                            Djupare
                          </Button>
                        </div>
                        <div className="h-1 bg-[#171717]">
                          <div
                            className="h-full bg-[#34C759]"
                            style={{ width: `${Math.max(5, pctW)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Session list */}
            {data.sessions.length > 0 && (
              <section className="card-tactical p-6">
                <div className="section-hdr mb-5">
                  <span className="marker" />
                  Matcher i turneringen
                </div>
                <div className="space-y-2" data-testid="tournament-sessions-list">
                  {data.sessions.map((s) => (
                    <div
                      key={s.id}
                      data-testid={`tournament-session-${s.id}`}
                      className="flex items-center gap-3 border border-[#27272A] p-3 flex-wrap"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-display uppercase tracking-widest text-white truncate">
                          {s.name}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {s.goalkeeper_name || "—"}
                          {s.opponent ? ` · vs ${s.opponent}` : ""}
                          {" · "}
                          {safeFormatDate(s.date)}
                        </div>
                      </div>
                      <div className="text-right text-xs text-zinc-400">
                        <span className="text-[#34C759] font-display font-bold">
                          {(s.stats_summary?.save_pct || 0).toFixed(0)}%
                        </span>
                        {" · "}
                        {s.stats_summary?.saves || 0}/{s.stats_summary?.total || 0}
                      </div>
                      <Button
                        onClick={() =>
                          navigate(`/gk/${s.goalkeeper_id}/evaluate/session/${s.id}`)
                        }
                        variant="outline"
                        className="h-9 rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white text-xs"
                      >
                        Öppna
                      </Button>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
